import {
  DefaultExcelAllowedTagsRegexMap,
  DefaultExcelRichTextAllowedTags,
  DefaultExcelSanitizeHtmlOptions,
} from "@constants/excel";
import { isEmpty, orderBy } from "lodash";
import sanitizeHtml from "sanitize-html";
import parse from "html-react-parser";
import { getMonth, getYear } from "date-fns";
import { CompareOperator } from "@models/core/compareOperator";

export function constructUserName(
  name?: string | null,
  firstName?: string | null,
  lastName?: string | null,
): string {
  if (!firstName && !lastName && !name) {
    return "";
  }
  if (isEmpty(firstName) && isEmpty(lastName)) return name ?? "";
  const nameArray = [firstName, lastName].filter(Boolean);
  return nameArray.join(" ");
}

export function parseJwt(token: string) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""),
  );

  return JSON.parse(jsonPayload);
}

export function generateGAvatarUrl(
  email?: string | null,
  firstName?: string | null,
  lastName?: string | null,
): string {
  const md5 = require("md5");

  const newEmail = email?.trim()?.toLowerCase();
  const letter = `${firstName?.charAt(0)}${lastName?.charAt(0)}`.toLowerCase();
  const defaultAvatar = `https://cdn.auth0.com/avatars/${letter}.png`;

  return `https://www.gravatar.com/avatar/${md5(newEmail)}?d=${defaultAvatar}`;
}

export const generateUUID = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const nameOfFactory =
  <T>() =>
  (name: keyof T) =>
    name;

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const sortAscByOrder = <T>(array: T[]) =>
  orderBy(array, ["order"], ["asc"]);

export const tryParseJSON = (data: string) => {
  try {
    const object = JSON.parse(data);
    if (object && typeof object === "object") {
      return object;
    }
  } catch {
    return false;
  }
  return false;
};

export const checkingDomain = (
  organizationDomain?: string,
  userDomain?: string,
) => {
  if (!organizationDomain || !userDomain) return false;

  const regex = new RegExp(`${organizationDomain}`, "i");
  return regex.test(userDomain);
};

export const generateSerialCode = (): string =>
  Date.now().toString(16).toUpperCase();

export const getExcelRichText = ({
  htmlString,
  sanitizeOptions = DefaultExcelSanitizeHtmlOptions,
  allowedTags = DefaultExcelRichTextAllowedTags,
  tagRegexMap = DefaultExcelAllowedTagsRegexMap,
}: {
  htmlString: string;
  sanitizeOptions?: sanitizeHtml.IOptions;
  allowedTags?: Record<string, { transformToNewLine: boolean }> | undefined;
  tagRegexMap?: Record<
    string,
    {
      open: RegExp;
      close: RegExp;
    }
  >;
}): string => {
  let sanitizedHtml = sanitizeHtml(htmlString, sanitizeOptions);
  if (isEmpty(sanitizedHtml) && !isEmpty(htmlString)) return htmlString;
  sanitizedHtml = transformTags({
    html: sanitizedHtml,
    tags: allowedTags,
    tagRegexMap: tagRegexMap,
  });
  return parse(sanitizedHtml, { trim: true }).toString();
};

const transformTags = ({
  html,
  tags,
  tagRegexMap,
}: {
  html: string;
  tags: Record<string, { transformToNewLine: boolean }>;
  tagRegexMap: Record<
    string,
    {
      open: RegExp;
      close: RegExp;
    }
  >;
}): string => {
  Object.entries(tags).forEach(([key, { transformToNewLine }]) => {
    const tagRegex = tagRegexMap?.[key];

    if (!tagRegex) return;

    if (!transformToNewLine) {
      html = html.replace(tagRegex.open, "").replace(tagRegex.close, "");

      return;
    }

    html = html
      .replace(tagRegex.open, key === "li" ? "\n- " : "\n")
      .replace(tagRegex.close, "");
  });

  return html.trim();
};

export const isSameMonthAndYear = (
  dateA: Date | null,
  dateB: Date | null,
): boolean => {
  if (!dateA || !dateB) return dateA === dateB;
  return (
    getMonth(dateA) === getMonth(dateB) && getYear(dateA) === getYear(dateB)
  );
};

export const compareNumberValuesByOperator = (
  symbol: CompareOperator,
  left: number,
  right: number,
): boolean => {
  switch (symbol) {
    case CompareOperator.GreaterThan:
      return left > right;
    case CompareOperator.LessThan:
      return left < right;
    case CompareOperator.EqualTo:
      return left === right;
    case CompareOperator.GreaterThanOrEqualTo:
      return left >= right;
    case CompareOperator.LessThanOrEqualTo:
      return left <= right;
    case CompareOperator.NotEqualTo:
      return left !== right;
    default:
      return false;
  }
};

export const compareBooleanValuesByOperator = (
  symbol: CompareOperator,
  left: boolean,
  right: boolean,
): boolean => {
  switch (symbol) {
    case CompareOperator.EqualTo:
      return left === right;
    case CompareOperator.NotEqualTo:
      return left !== right;
    default:
      return false;
  }
};

export const omitFieldFromObject = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic object
  T extends Record<string, any>,
  K extends keyof T,
>(
  obj: T,
  fieldKey: K,
): Omit<T, K> => {
  const { [fieldKey]: _, ...rest } = obj;
  return rest as Omit<T, K>;
};
